import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.overlay}>
          <div style={styles.card}>
            <span style={styles.icon}>⚠️</span>
            <h1 style={styles.heading}>Something went wrong</h1>
            <p style={styles.message}>
              {this.state.error?.message || "An unexpected error occurred."}
            </p>
            <button
              style={styles.button}
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    padding: 16,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 8,
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.12)",
    padding: "48px 40px",
    maxWidth: 480,
    width: "100%",
    textAlign: "center",
  },
  icon: {
    fontSize: 48,
    display: "block",
    marginBottom: 16,
  },
  heading: {
    fontSize: 22,
    fontWeight: 600,
    color: "#1a1a1a",
    margin: "0 0 8px",
  },
  message: {
    fontSize: 14,
    color: "#666666",
    margin: "0 0 24px",
    lineHeight: 1.5,
    wordBreak: "break-word",
  },
  button: {
    backgroundColor: "#0d6efd",
    color: "#ffffff",
    border: "none",
    borderRadius: 6,
    padding: "10px 28px",
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
  },
};

export default ErrorBoundary;
